<template>
  <div>
    <div class="progress">
      <span class="current" :style="{ width: `${progress}%` }">
        {{ progress }}%
      </span>
    </div>

    <div class="steps row">
      <div class="col" :class="{ active: step >= 1 }">
        1. Remortgage
      </div>
      <div class="col" :class="{ active: step >= 2 }">
        2. Your income & credit history
      </div>
      <div class="col" :class="{ active: step >= 3 }">
        3. Personal Information
      </div>
    </div>

    <div class="container">
      <transition name="v-fade" mode="out-in">
        <div v-if="step === 1" key="step1">
          <ValidationObserver v-slot="{ handleSubmit }">
            <form @submit.prevent="handleSubmit(nextStep)">
              <p class="step-heading">
                What type of Remortgage are you looking for?
              </p>

              <div class="limited">
                <div class="form-group">
                  <p class="question">
                    Remortgage Purpose
                  </p>

                  <toggles
                    v-model="form.data3"
                    :options="['Residential', 'BTL', 'Commercial', 'Other']"
                    rules="required"
                    name="purpose"
                    :in-row="4"
                  />
                </div>

                <div class="form-group">
                  <p class="question">What is the property value?</p>

                  <vue-input
                    v-model="form.data1"
                    name="property cost"
                    type="number"
                    :placeholder="$currency"
                    rules="required"
                  />
                </div>

                <div class="form-group">
                  <p class="question">How much are you looking to borrow?</p>

                  <vue-input
                    v-model="form.data2"
                    type="number"
                    name="mortgage value"
                    :placeholder="$currency"
                    rules="required"
                  />
                </div>

                <div class="form-group">
                  <p class="question">
                    How quickly are you looking to proceed?
                  </p>

                  <toggles
                    v-model="form.data4"
                    :options="[
                      'Today',
                      'This week',
                      'This month',
                      'In the next 3 months',
                      'Later'
                    ]"
                    rules="required"
                    name="time"
                  />
                </div>

                <div class="sticky-submit">
                  <div class="row">
                    <div class="col-6">
                      <button
                        type="button"
                        class="btn btn-outline-primary btn btn-block"
                        @click="back"
                      >
                        Back
                      </button>
                    </div>
                    <div class="col-6">
                      <button type="submit" class="btn btn-secondary btn-block">
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </ValidationObserver>
        </div>

        <div v-else-if="step === 2" key="step2">
          <ValidationObserver v-slot="{ handleSubmit }">
            <form class="limited" @submit.prevent="handleSubmit(nextStep)">
              <p class="step-heading">Your income & credit history</p>
              <div class="form-group">
                <p class="question">
                  Do you have any adverse credit?
                </p>

                <p class="help">
                  Adverse credit would include late payments / defaults / CCJs /
                  Mortgage arrears / IVAs / or Bankruptcy, on any overdraft /
                  credit card / loan / secured loan / mortgage etc.
                </p>

                <toggles
                  v-model="form.data5"
                  :options="['Yes', 'No']"
                  rules="required"
                  name="credit"
                />
              </div>

              <div class="form-group">
                <p class="question">
                  What type of credit issue have you experienced?
                </p>

                <p class="help">
                  you can pick more than one
                </p>

                <toggles
                  v-model="form.data6"
                  :options="[
                    'Low Credit Score',
                    'Default',
                    'CCJs',
                    'IVAs',
                    'Late Payments (Mortgage)',
                    'Late Payments (Not Mortgage)',
                    'Debt Management Plan',
                    'Repossession',
                    'Bankruptcy'
                  ]"
                  rules="required"
                  name="credit"
                  :multiple="true"
                />
              </div>

              <div class="sticky-submit">
                <div class="row">
                  <div class="col-6">
                    <button
                      type="button"
                      class="btn btn-outline-primary btn btn-block"
                      @click="back"
                    >
                      Back
                    </button>
                  </div>
                  <div class="col-6">
                    <button type="submit" class="btn btn-secondary btn-block">
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </ValidationObserver>
        </div>

        <div v-else-if="step === 3" key="step3">
          <ValidationObserver v-slot="{ handleSubmit }">
            <form class="limited-lg" @submit.prevent="handleSubmit(submit)">
              <p class="step-heading">
                Personal information
              </p>

              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <vue-input
                      v-model="form.firstName"
                      name="first name"
                      placeholder="First Name"
                      rules="required"
                      label="First Name"
                    />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <vue-input
                      v-model="form.lastName"
                      name="surname"
                      placeholder="Surname"
                      rules="required"
                      label="Surname"
                    />
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <vue-input
                      v-model="form.phone"
                      type="tel"
                      name="phone"
                      placeholder="Phone"
                      rules="required|tel"
                      label="Phone"
                    />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <vue-input
                      v-model="form.email"
                      type="email"
                      name="email"
                      placeholder="Email"
                      rules="required|email"
                      label="Email Address"
                    />
                  </div>
                </div>
              </div>

              <div class="form-group">
                <vue-input
                  v-model="form.postcode"
                  type="postcode"
                  name="postcode"
                  placeholder="Postcode"
                  rules="required|postcode"
                  label="Postcode"
                />
              </div>

              <div class="sticky-submit">
                <div class="row">
                  <div class="col-6">
                    <button
                      type="button"
                      class="btn btn-outline-primary btn btn-block"
                      @click="back"
                    >
                      Back
                    </button>
                  </div>
                  <div class="col-6">
                    <button
                      type="submit"
                      class="btn btn-secondary btn-block"
                      :class="{ loader: loading }"
                      :disabled="loading"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </ValidationObserver>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import VueInput from "./VueInput.vue"
import Toggles from "./Toggles.vue"
import { getFormData } from "../utils"

export default {
  components: { Toggles, VueInput },

  props: ["formId", "resultPage"],

  data() {
    return {
      stepsCount: 4,
      step: 1,
      form: {
        purpose: "remortgage"
      },
      loading: false
    }
  },

  computed: {
    progress() {
      return Math.round((this.step / this.stepsCount) * 100)
    }
  },

  watch: {
    step() {
      window.scrollTo(0, 0)
    }
  },

  mounted() {
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of new URLSearchParams(window.location.search)) {
      this.form = {
        ...this.form,
        [key]: value
      }
    }
  },

  methods: {
    nextStep() {
      this.step += 1
    },

    back() {
      if (this.step > 1) {
        this.step -= 1
      } else {
        this.exit()
      }
    },

    exit() {
      window.location.href = "/"
    },

    submit() {
      this.loading = true

      if (window.fbq) {
        window.fbq("track", "Lead")
      }

      if (window.dataLayer) {
        window.dataLayer.push({ event: "lead" })
      }

      $.ajax({
        url: `/wp-json/contact-form-7/v1/contact-forms/${this.formId}/feedback`,
        method: "POST",
        dataType: "json",
        data: getFormData(this.form),
        processData: false,
        contentType: false,
        success: res => {
          if (res.status !== "mail_sent") {
            alert(
              "We unfortunately couldn't process your submission. Please try again."
            )
          } else if (this.resultPage) {
            window.location.href = this.resultPage
          }
          this.loading = false
        },
        error: () => {
          alert(
            "We unfortunately couldn't process your submission. Please try again."
          )
          this.loading = false
        }
      })
    }
  }
}
</script>
